import { useCallback, useState } from 'react';
import type { NavigationProp } from '@react-navigation/native';
import { useIsFocused } from '@react-navigation/native';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { DATABASE_UNAVAILABLE_USER_MESSAGE, isDatabaseUnavailableError } from '../../../shared/localDb';
import { getSessionOrganizationId, getMiscProductIdForOrganization } from '../../../shell/session/sessionOrganization';
import { showError, showInfo } from '../../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { useOperationalConfirm } from '../../../shared/ui/useOperationalConfirm';

export function usePosVariosItem(options: {
  canEdit: boolean;
  variosName: string;
  variosAmount: string;
  editingCustomLineId: string | null;
  closeVarios: () => void;
  addCustomItemToCart: (input: { name: string; amount: number; miscProductId: string }) => void;
  updateCustomCartLine: (lineId: string, name: string, amount: number) => void;
  navigation: NavigationProp<Record<string, object | undefined>>;
}) {
  const { canEdit, variosName, variosAmount, editingCustomLineId, closeVarios, addCustomItemToCart, updateCustomCartLine, navigation } = options;
  const [variosSaving, setVariosSaving] = useState(false);
  const isFocused = useIsFocused();
  const { confirm, ConfirmModal } = useOperationalConfirm();

  const saveVariousItem = useCallback(async () => {
    if (!canEdit) {
      showInfo('Modo solo lectura', 'Podés mirar el POS, no cobrar.');
      return;
    }
    if (variosSaving) return;

    const amount = parseDecimalInput(variosAmount, { min: 0 });
    const name = variosName.trim() || 'Venta Varios';
    if (amount === null || amount <= 0) {
      showError('Monto inválido', 'Ingresá un monto válido mayor a cero (ej. 1500 o 1500,50).');
      return;
    }

    setVariosSaving(true);
    try {
      // Gap 5: edit mode — actualizar línea existente.
      if (editingCustomLineId) {
        updateCustomCartLine(editingCustomLineId, name, amount);
        closeVarios();
        return;
      }
      const orgId = await getSessionOrganizationId();
      if (!orgId) {
        showError('Sesión', 'No hay organización. Revisá la conexión o volvé a entrar.');
        return;
      }
      const miscProductId = await getMiscProductIdForOrganization(orgId);
      if (!miscProductId) {
        const ok = await confirm({
          title: 'Falta configurar Varios',
          message:
            'No está la línea Varios. Creala en Catálogo o sincronizá si ya la tenés.',
          confirmLabel: 'Abrir Catálogo',
        });
        if (ok) navigation.navigate('Catalog');
        return;
      }
      if (!isFocused) return;
      addCustomItemToCart({ name, amount, miscProductId });
      closeVarios();
    } catch (e) {
      if (isDatabaseUnavailableError(e)) {
        showError('Venta rápida', DATABASE_UNAVAILABLE_USER_MESSAGE);
        return;
      }
      presentUnexpectedFailure({ module: 'pos', op: 'varios_item.save', error: e, userMessage: 'No se pudo agregar el item.' });
    } finally {
      setVariosSaving(false);
    }
  }, [
    addCustomItemToCart,
    canEdit,
    closeVarios,
    confirm,
    editingCustomLineId,
    isFocused,
    navigation,
    variosAmount,
    variosName,
    variosSaving,
    updateCustomCartLine,
  ]);

  return { variosSaving, saveVariousItem, ConfirmModal };
}
